/**
 * Upgrade-page retailer price module view model.
 * Uses existing 48-hour freshness classification.
 * Does not reuse freshest-timestamp ranking among CURRENT tied offers.
 * Commission is not an input.
 */

import {
  formatHeroCurrency,
  getComparableOfferPrice,
  getRetailerNameFromOffer,
  pricesMatch,
} from '../PriceComparison/simpleProductPageShared';
import {
  OFFER_FRESHNESS_STATE,
  STALE_PRICE_DISCLAIMER,
  getOfferFreshnessState,
  getOfferVerificationTimestamp,
  isExplicitlyUnavailable,
} from '../../domain/offerFreshness';
import { compareRetailerPreference } from '../../domain/retailerPreference';
import { getTrackedAmazonLink } from '../../domain/amazonTrackedLinks';

const CENTRAL_TIME = 'America/Chicago';

const EXPLICIT_IN_STOCK = new Set([
  'in stock',
  'in-stock',
  'in_stock',
  'instock',
  'available',
]);

export function getCustomerAffiliateHref(offer) {
  if (!offer) return '';
  const href = offer.affiliate_url || offer['Job Link'] || offer.affiliate_link || '';
  if (!href || typeof href !== 'string') return '';
  const trimmed = href.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return getTrackedAmazonLink(trimmed);
  } catch {
    return '';
  }
}

export function isKnownInStockOffer(offer) {
  const raw = offer?.availability_status || offer?.availability || offer?.Availability;
  if (!raw || typeof raw !== 'string') return false;
  return EXPLICIT_IN_STOCK.has(raw.trim().toLowerCase());
}

export function formatVerifiedDateOnly(offer) {
  const ts = getOfferVerificationTimestamp(offer);
  if (ts == null) return null;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: CENTRAL_TIME,
    month: 'short',
    day: 'numeric',
  }).format(new Date(ts));
}

function compareCurrentRetailers(a, b) {
  const priceA = getComparableOfferPrice(a) ?? Infinity;
  const priceB = getComparableOfferPrice(b) ?? Infinity;
  if (priceA !== priceB) return priceA - priceB;

  const pref = compareRetailerPreference(
    getRetailerNameFromOffer(a),
    getRetailerNameFromOffer(b)
  );
  if (pref !== 0) return pref;

  return getCustomerAffiliateHref(a).localeCompare(getCustomerAffiliateHref(b));
}

function compareStaleRetailers(a, b) {
  const priceA = getComparableOfferPrice(a) ?? Infinity;
  const priceB = getComparableOfferPrice(b) ?? Infinity;
  if (priceA !== priceB) return priceA - priceB;
  const pref = compareRetailerPreference(
    getRetailerNameFromOffer(a),
    getRetailerNameFromOffer(b)
  );
  if (pref !== 0) return pref;
  return getCustomerAffiliateHref(a).localeCompare(getCustomerAffiliateHref(b));
}

function isModuleEligibleOffer(offer) {
  if (!offer || offer.is_active === false) return false;
  if (getComparableOfferPrice(offer) == null) return false;
  if (isExplicitlyUnavailable(offer)) return false;
  return Boolean(getCustomerAffiliateHref(offer));
}

export function getSharedPrimaryVerifiedLine(primaryOffers) {
  if (!Array.isArray(primaryOffers) || primaryOffers.length < 2) return null;
  const dates = primaryOffers.map(formatVerifiedDateOnly);
  if (dates.some((dateLabel) => !dateLabel)) return null;
  const sharedDate = dates[0];
  if (!dates.every((dateLabel) => dateLabel === sharedDate)) return null;
  return `All ${primaryOffers.length} prices verified ${sharedDate}`;
}

function toRetailerOption(offer, extras) {
  const retailer = getRetailerNameFromOffer(offer);
  const href = getCustomerAffiliateHref(offer);
  const price = getComparableOfferPrice(offer);
  const dateLabel = formatVerifiedDateOnly(offer);
  const isCurrent = extras.freshnessState === OFFER_FRESHNESS_STATE.CURRENT;
  const verifiedLine = dateLabel
    ? isCurrent
      ? `Verified ${dateLabel}`
      : `Last verified ${dateLabel}`
    : null;
  return {
    offer,
    retailer,
    href,
    price,
    priceLabel: formatHeroCurrency(price),
    freshnessState: extras.freshnessState,
    verifiedDateLabel: dateLabel,
    verifiedLine,
    showVerifiedInControl: extras.showVerifiedInControl !== false && Boolean(verifiedLine),
    isCurrent,
    isLowestPrice: Boolean(extras.isLowestPrice),
    isTiedLowest: Boolean(extras.isTiedLowest),
    isKnownInStock: isKnownInStockOffer(offer),
    showPriceInControl: Boolean(extras.showPriceInControl),
    lastKnownPriceLine: isCurrent || price == null ? null : `Last known price ${formatHeroCurrency(price)}`,
    lastKnownDisclaimer: isCurrent ? null : STALE_PRICE_DISCLAIMER,
    ariaLabel: isCurrent
      ? `Check price & availability at ${retailer}`
      : `Check current price & availability at ${retailer}`,
    position: extras.position,
  };
}

export function getRetailerPriceModule(offers, options = {}) {
  const now = options.now;
  const list = (Array.isArray(offers) ? offers : []).filter(isModuleEligibleOffer);
  const current = [];
  const stale = [];

  list.forEach((offer) => {
    const state = getOfferFreshnessState(offer, now);
    if (state === OFFER_FRESHNESS_STATE.CURRENT) current.push(offer);
    else if (state === OFFER_FRESHNESS_STATE.STALE) stale.push(offer);
  });

  current.sort(compareCurrentRetailers);
  stale.sort(compareStaleRetailers);

  const lowestPrice = current.length ? getComparableOfferPrice(current[0]) : null;
  const primaryOffers =
    lowestPrice == null
      ? []
      : current.filter((offer) => pricesMatch(getComparableOfferPrice(offer), lowestPrice));
  const otherCurrentOffers = current.filter(
    (offer) => !pricesMatch(getComparableOfferPrice(offer), lowestPrice)
  );
  const isTiedLowest = primaryOffers.length > 1;
  const hasCurrent = primaryOffers.length > 0;
  const sharedPrimaryVerifiedLine = getSharedPrimaryVerifiedLine(primaryOffers);

  let position = 0;
  const assign = (offer, extras) => {
    position += 1;
    return toRetailerOption(offer, { ...extras, position });
  };

  const primaryRetailers = primaryOffers.map((offer) =>
    assign(offer, {
      freshnessState: OFFER_FRESHNESS_STATE.CURRENT,
      isLowestPrice: true,
      isTiedLowest,
      showPriceInControl: false,
      showVerifiedInControl: !sharedPrimaryVerifiedLine,
    })
  );
  const otherCurrentRetailers = otherCurrentOffers.map((offer) =>
    assign(offer, {
      freshnessState: OFFER_FRESHNESS_STATE.CURRENT,
      isLowestPrice: false,
      isTiedLowest: false,
      showPriceInControl: true,
    })
  );
  const staleRetailers = stale.map((offer) =>
    assign(offer, {
      freshnessState: OFFER_FRESHNESS_STATE.STALE,
      isLowestPrice: false,
      isTiedLowest: false,
      showPriceInControl: true,
    })
  );

  let availabilityLine = 'No current verified price';
  if (hasCurrent && primaryRetailers.length === 1) {
    availabilityLine = `Available at ${primaryRetailers[0].retailer}`;
  } else if (hasCurrent && primaryRetailers.length > 1) {
    availabilityLine = `Available at ${primaryRetailers.length} retailers at this price`;
  }

  return {
    hasCurrent,
    isTiedLowest,
    lowestPrice,
    lowestPriceLabel: hasCurrent ? formatHeroCurrency(lowestPrice) : null,
    primaryCount: primaryRetailers.length,
    availabilityLine,
    primaryKicker: hasCurrent ? 'Current lowest verified price' : 'No current verified price',
    otherCurrentHeading: 'Other current retailers',
    staleHeading: staleRetailers.length === 1 ? 'Other retailer' : 'Other retailers',
    primaryRetailers,
    otherCurrentRetailers,
    staleRetailers,
    sharedPrimaryVerifiedLine,
    visibleRetailers: [...primaryRetailers, ...otherCurrentRetailers, ...staleRetailers],
  };
}
